import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../services/api';
import { useToast } from '../hooks/useToast';

import aiIcon from '../adminGallery/artificial-intelligence.png';
// ------ Icons for the removed config ----
// import agentIcon from '../adminGallery/bot.png';
// import ragIcon from '../adminGallery/embedded.png';
// import costIcon from '../adminGallery/money (1).png';
import deployIcon from '../adminGallery/startup.png';
import gitIcon from '../adminGallery/git (1).png';
import oauthIcon from '../adminGallery/insurance.png';
import billingIcon from '../adminGallery/bill.png';
// import langsmithIcon from '../adminGallery/chain.png'; // ----- langsmith icon removed -----
import mediaIcon from '../adminGallery/image-gallery.png';

const SECTION_ICONS = {
  'ai-providers': aiIcon,
  // ---- removed sections - configuration ----------
  // 'agent-config': agentIcon,
  // 'rag': ragIcon,
  // 'cost': costIcon,
  'deployment': deployIcon,
  'git': gitIcon,
  'oauth': oauthIcon,
  'billing': billingIcon,
  // 'langsmith': langsmithIcon,  // -----langsmith config --------
  'media': mediaIcon,
};


const SECRET_KEYS = new Set([
  'OPENAI_API_KEY', 'GOOGLE_API_KEY', 'ANTHROPIC_API_KEY',
  'GITHUB_TOKEN', 'BITBUCKET_TOKEN', 'GITLAB_TOKEN',
  'VERCEL_TOKEN', 'NETLIFY_TOKEN', 'RAILWAY_TOKEN',
  'GITHUB_CLIENT_SECRET', 'BITBUCKET_CLIENT_SECRET',
  'STRIPE_SECRET_KEY', 'STRIPE_WEBHOOK_SECRET',
  'LANGSMITH_API_KEY', 'UNSPLASH_ACCESS_KEY',
]);

// const REQUIRED_FIELDS = ['LLM_PROVIDER', 'AGENT_FRAMEWORK', 'CODING_MODEL', 'VALIDATOR_MODEL'];
const REQUIRED_FIELDS = ['LLM_PROVIDER'];
export default function AdminConfigSettings() {
  const [config, setConfig] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [errors, setErrors] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const toast = useToast();
  const sectionRefs = useRef({});
  const hasUnsaved = JSON.stringify(config) !== JSON.stringify(original);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, meRes] = await Promise.all([
        authFetch('/api/admin/configurations'),
        authFetch('/api/user/me'),
      ]);
      if (!cfgRes.ok) throw new Error('Failed to load');
      const data = await cfgRes.json();
      setConfig(data);
      setOriginal(data);
      if (meRes.ok) {
        const me = await meRes.json();
        setIsAdmin(me.role === 'admin');
      }
    } catch (e) {
      toast.error('Failed to load configuration');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  const setValue = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const validate = () => {
    const errs = {};
    for (const key of REQUIRED_FIELDS) {
      if (!config[key] || !config[key].trim()) errs[key] = 'This field is required';
    }
    // const numFields = ['MAX_STEPS_PER_JOB', 'MAX_TOKENS_PER_JOB', 'RAG_MAX_CONTEXT_TOKENS', 'MONTHLY_BUDGET_USD'];
    const numFields = [];
    for (const key of numFields) {
      if (config[key] && Number(config[key]) <= 0) errs[key] = 'Must be greater than 0';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error('Please fix validation errors'); return; }
    setSaving(true);
    try {
      const res = await authFetch('/api/admin/configurations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Configuration updated successfully.');
      setOriginal({ ...config });
    } catch (e) {
      toast.error('Failed to save configuration');
    }
    setSaving(false);
  };

  const toggleSecret = (key) => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div className="acfg-page">
        <div className="acfg-skeleton-wrap">
          {[1,2,3,4].map(i => <div key={i} className="acfg-skeleton" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="acfg-page">
      <style>{CSS}</style>
      <div className="acfg-header">
        <div>
          <h2 className="acfg-title">Configurations</h2>
          <p className="acfg-desc">{isAdmin ? 'Manage platform-wide configuration settings.' : 'Manage your personal configuration settings. These override platform defaults for your usage only.'}</p>
        </div>
        <button className={`acfg-save-btn${hasUnsaved ? ' acfg-save-btn--active' : ''}`} onClick={handleSave} disabled={saving || !hasUnsaved}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {!isAdmin && <div className="acfg-personal-banner">ℹ️ You are editing your personal configuration. Changes here apply only to your account and do not affect platform settings.</div>}

      <div className="acfg-body">

        <div className="acfg-content">
          {/* Section 1: AI Providers */}
          <Section id="ai-providers" title="AI Providers" refs={sectionRefs}>
            <Field label="LLM Provider" field="LLM_PROVIDER" type="select" options={['openai','google_gemini','anthropic']} config={config} setValue={setValue} errors={errors} />
            <ProviderGroup label="OpenAI Settings" enabled={config.LLM_PROVIDER === 'openai'}>
              <Field label="OpenAI API Key" field="OPENAI_API_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} disabled={config.LLM_PROVIDER !== 'openai'} />
              <Field label="OpenAI Model" field="OPENAI_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'openai'} />
              <Field label="Coding Model" field="CODING_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'openai'} />
              <Field label="Validator Model" field="VALIDATOR_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'openai'} />
            </ProviderGroup>
            <ProviderGroup label="Google Gemini Settings" enabled={config.LLM_PROVIDER === 'google_gemini'}>
              <Field label="Google API Key" field="GOOGLE_API_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Google Model" field="GOOGLE_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Gemini Model" field="GEMINI_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Gemini Fallback Model" field="GEMINI_FALLBACK_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Gemini Enabled" field="GEMINI_ENABLED" type="toggle" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Coding Model (optional override)" field="GOOGLE_CODING_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
              <Field label="Validator Model (optional override)" field="GOOGLE_VALIDATOR_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'google_gemini'} />
            </ProviderGroup>
            <ProviderGroup label="Anthropic Settings" enabled={config.LLM_PROVIDER === 'anthropic'}>
              <Field label="Anthropic API Key" field="ANTHROPIC_API_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} disabled={config.LLM_PROVIDER !== 'anthropic'} />
              <Field label="Anthropic Model" field="ANTHROPIC_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'anthropic'} />
              <Field label="Coding Model (optional override)" field="ANTHROPIC_CODING_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'anthropic'} />
              <Field label="Validator Model (optional override)" field="ANTHROPIC_VALIDATOR_MODEL" config={config} setValue={setValue} errors={errors} disabled={config.LLM_PROVIDER !== 'anthropic'} />
            </ProviderGroup>
          </Section> 
 
          {/* Section 2: Agent Configuration */}
          {/* <Section id="agent-config" title="Agent Configuration" refs={sectionRefs}>
            <Field label="Agent Framework" field="AGENT_FRAMEWORK" type="select" options={['native','crewai']} config={config} setValue={setValue} errors={errors} />
            <Field label="Max Steps Per Job" field="MAX_STEPS_PER_JOB" type="number" config={config} setValue={setValue} errors={errors} />
            <Field label="Max Tokens Per Job" field="MAX_TOKENS_PER_JOB" type="number" config={config} setValue={setValue} errors={errors} />
            <Field label="Validator Model" field="VALIDATOR_MODEL" config={config} setValue={setValue} errors={errors} />
            <Field label="Coding Model" field="CODING_MODEL" config={config} setValue={setValue} errors={errors} />
          </Section> */}

          {/* Section 3: RAG & Embeddings */}
          {/* <Section id="rag" title="RAG & Embeddings" refs={sectionRefs}>
            <Field label="Embedding Model" field="EMBEDDING_MODEL" config={config} setValue={setValue} errors={errors} />
            <Field label="RAG Max Context Tokens" field="RAG_MAX_CONTEXT_TOKENS" type="number" config={config} setValue={setValue} errors={errors} />
          </Section> */}

          {/* Section 4: Cost Controls */}
          {/* <Section id="cost" title="Cost Controls" refs={sectionRefs}>
            <Field label="Monthly Platform Budget (USD)" field="MONTHLY_BUDGET_USD" type="number" prefix="$" config={config} setValue={setValue} errors={errors} />
          </Section> */}

          {/* Section 5: Deployment */}
          <Section id="deployment" title="Deployment" refs={sectionRefs}>
            <Field label="Require Approval" field="REQUIRE_APPROVAL" type="toggle" config={config} setValue={setValue} errors={errors} />
            <Divider label="Deployment Provider Tokens" />
            <ProviderTokenStatus
              label="Vercel"
              field="VERCEL_TOKEN"
              config={config}
              description="Required for Vercel deployments and automatic custom domain attachment"
            />
            <Field label="Vercel Token" field="VERCEL_TOKEN" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <ProviderTokenStatus
              label="Netlify"
              field="NETLIFY_TOKEN"
              config={config}
              description="Required for Netlify deployments and automatic custom domain attachment"
            />
            <Field label="Netlify Token" field="NETLIFY_TOKEN" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <ProviderTokenStatus
              label="Railway"
              field="RAILWAY_TOKEN"
              config={config}
              description="Required for Railway deployments and automatic custom domain attachment"
            />
            <Field label="Railway Token" field="RAILWAY_TOKEN" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 8, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              ℹ️ Once tokens are saved, all custom domain operations (DNS verification, provider attachment, SSL provisioning) run fully automatically. No further administrator action is required.
            </div>
          </Section>



          {/* Section 7: OAuth Configuration */}
          <Section id="oauth" title="OAuth Configuration" refs={sectionRefs}>
            <Divider label="GitHub OAuth" />
            <Field label="GitHub Client ID" field="GITHUB_CLIENT_ID" config={config} setValue={setValue} errors={errors} />
            <Field label="GitHub Client Secret" field="GITHUB_CLIENT_SECRET" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <Field label="Callback URL (paste into GitHub app settings)" field="GITHUB_CALLBACK_URL" type="readonly" config={config} setValue={setValue} errors={errors} />
            <Divider label="Bitbucket OAuth" />
            <Field label="Bitbucket Client ID" field="BITBUCKET_CLIENT_ID" config={config} setValue={setValue} errors={errors} />
            <Field label="Bitbucket Client Secret" field="BITBUCKET_CLIENT_SECRET" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <Field label="Callback URL (paste into Bitbucket app settings)" field="BITBUCKET_CALLBACK_URL" type="readonly" config={config} setValue={setValue} errors={errors} />
          </Section>

          {/* Section 7b: Rate Limiting */}
          <Section id="rate-limiting" title="Rate Limiting" refs={sectionRefs}>
            <Field label="High-Cost Endpoints (e.g. 10/minute) — /generate, /deploy" field="RATE_LIMIT_HIGH_COST" config={config} setValue={setValue} errors={errors} />
            <Field label="Lightweight Endpoints (e.g. 200/minute) — /auth, /health" field="RATE_LIMIT_LIGHTWEIGHT" config={config} setValue={setValue} errors={errors} />
          </Section>

          {/* Section 8: Billing & Stripe */}
          <Section id="billing" title="Billing & Stripe" refs={sectionRefs}>
            <Field label="Enable Billing" field="ENABLE_BILLING" type="toggle" config={config} setValue={setValue} errors={errors} />
            <Field label="Stripe Secret Key" field="STRIPE_SECRET_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <Field label="Stripe Webhook Secret" field="STRIPE_WEBHOOK_SECRET" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <Field label="Stripe Price - Starter" field="STRIPE_PRICE_STARTER" config={config} setValue={setValue} errors={errors} />
            <Field label="Stripe Price - Pro" field="STRIPE_PRICE_PRO" config={config} setValue={setValue} errors={errors} />
            <Field label="Stripe Price - Team" field="STRIPE_PRICE_TEAM" config={config} setValue={setValue} errors={errors} />
          </Section>

          {/* Section 9: LangSmith */}
          {/* <Section id="langsmith" title="LangSmith" refs={sectionRefs}>
            <Field label="LangChain Project" field="LANGCHAIN_PROJECT" config={config} setValue={setValue} errors={errors} />
            <Field label="LangSmith Tracing" field="LANGSMITH_TRACING" type="toggle" config={config} setValue={setValue} errors={errors} />
            <Field label="LangChain Tracing V2" field="LANGCHAIN_TRACING_V2" type="toggle" config={config} setValue={setValue} errors={errors} />
            <Field label="LangSmith API Key" field="LANGSMITH_API_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
            <Field label="LangChain Endpoint" field="LANGCHAIN_ENDPOINT" config={config} setValue={setValue} errors={errors} />
          </Section> */}

          {/* Section 10: Media & Assets */}
          <Section id="media" title="Media & Assets" refs={sectionRefs}>
            <Field label="Unsplash Access Key" field="UNSPLASH_ACCESS_KEY" type="secret" config={config} setValue={setValue} errors={errors} showSecrets={showSecrets} toggleSecret={toggleSecret} />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ id, title, refs, children }) {
  const icon = SECTION_ICONS[id];
  return (
    <div className="acfg-section" ref={el => { if (refs) refs.current[id] = el; }} id={`cfg-${id}`}>
      <h3 className="acfg-section-title">
        {icon && <img src={icon} alt="" className="acfg-section-icon" />}
        {title}
      </h3>
      <div className="acfg-section-fields">{children}</div>
    </div>
  );
}

function Divider({ label }) {
  return <div className="acfg-divider"><span>{label}</span></div>;
}

function ProviderTokenStatus({ label, field, config, description }) {
  const hasToken = !!(config[field] && config[field].trim());
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 18, height: 18, borderRadius: '50%', fontSize: 10, fontWeight: 700, flexShrink: 0,
        background: hasToken ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)',
        color: hasToken ? '#10b981' : '#64748b',
        border: `1px solid ${hasToken ? 'rgba(16,185,129,0.3)' : 'rgba(100,116,139,0.2)'}`,
      }}>
        {hasToken ? '✓' : '○'}
      </span>
      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: hasToken ? '#10b981' : 'var(--text-muted)' }}>
          {label} {hasToken ? '— configured' : '— not configured'}
        </span>
        {description && (
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '1px 0 0', lineHeight: 1.4 }}>{description}</p>
        )}
      </div>
    </div>
  );
}

function ProviderGroup({ label, enabled, children }) {
  return (
    <div className={`acfg-provider-group${enabled ? ' acfg-provider-group--active' : ''}`}>
      <div className="acfg-divider"><span>{label}{!enabled && ' (disabled)'}</span></div>
      <div className="acfg-provider-group-fields">{children}</div>
    </div>
  );
}

function Field({ label, field, type = 'text', options, prefix, config, setValue, errors, showSecrets, toggleSecret, disabled }) {
  const value = config[field] || '';
  const error = errors[field];
  const isSecret = SECRET_KEYS.has(field);
  const revealed = showSecrets?.[field];

  if (type === 'toggle') {
    const on = value === 'true' || value === true;
    return (
      <div className={`acfg-field${disabled ? ' acfg-field--disabled' : ''}`}>
        <label className="acfg-label">{label}</label>
        <div className="acfg-toggle-wrap">
          <button className={`acfg-toggle${on ? ' acfg-toggle--on' : ''}`} onClick={() => !disabled && setValue(field, on ? 'false' : 'true')} disabled={disabled}>
            <span className="acfg-toggle-knob" />
          </button>
          <span className={`acfg-toggle-label${on ? ' acfg-toggle-label--on' : ''}`}>{on ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div className={`acfg-field${disabled ? ' acfg-field--disabled' : ''}`}>
        <label className="acfg-label">{label}</label>
        <select className={`acfg-input${error ? ' acfg-input--error' : ''}`} value={value} onChange={e => setValue(field, e.target.value)} disabled={disabled}>
          <option value="">Select...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <span className="acfg-error">{error}</span>}
      </div>
    );
  }

  if (type === 'secret' || isSecret) {
    return (
      <div className={`acfg-field${disabled ? ' acfg-field--disabled' : ''}`}>
        <label className="acfg-label">{label}</label>
        <div className="acfg-secret-wrap">
          <input
            className={`acfg-input${error ? ' acfg-input--error' : ''}`}
            type={revealed ? 'text' : 'password'}
            value={value}
            onChange={e => setValue(field, e.target.value)}
            placeholder="••••••••"
            disabled={disabled}
          />
          <button className="acfg-secret-toggle" onClick={() => toggleSecret(field)} disabled={disabled}>
            {revealed ? 'Hide' : 'Show'}
          </button>
        </div>
        {error && <span className="acfg-error">{error}</span>}
      </div>
    );
  }

  if (type === 'readonly') {
    return (
      <div className="acfg-field">
        <label className="acfg-label">{label}</label>
        <div className="acfg-secret-wrap">
          <input className="acfg-input acfg-input--readonly" type="text" value={value} readOnly disabled />
          <button
            type="button"
            className="acfg-secret-toggle"
            onClick={() => value && navigator.clipboard?.writeText(value)}
          >
            Copy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`acfg-field${disabled ? ' acfg-field--disabled' : ''}`}>
      <label className="acfg-label">{label}</label>
      <div className="acfg-input-wrap">
        {prefix && <span className="acfg-prefix">{prefix}</span>}
        <input
          className={`acfg-input${error ? ' acfg-input--error' : ''}${prefix ? ' acfg-input--prefixed' : ''}`}
          type={type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={e => setValue(field, e.target.value)}
          disabled={disabled}
        />
      </div>
      {error && <span className="acfg-error">{error}</span>}
    </div>
  );
}

const CSS = `
.acfg-page { padding: 32px 0; max-width: 1100px; margin: 0 auto; }
.acfg-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; gap: 16px; flex-wrap: wrap; }
.acfg-title {
  font-size: 22px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.2;
  color: rgba(241,245,249,0.96); margin: 0 0 5px;
}
[data-theme="light"] .acfg-title { color: #0f172a; }
.acfg-desc {
  font-size: 13px; font-weight: 400; line-height: 1.55;
  color: rgba(148,163,184,0.7); margin: 0;
}
[data-theme="light"] .acfg-desc { color: #64748b; }
/* ── Save button — primary glass CTA ── */
.acfg-save-btn {
  padding: 10px 22px; border-radius: 9px;
  border: 1px solid rgba(99,102,241,0.22);
  background: rgba(6,9,22,0.55);
  color: rgba(148,163,184,0.5);
  font-size: 13px; font-weight: 600; letter-spacing: 0.02em;
  cursor: not-allowed;
  opacity: 1;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.15s, color 0.2s;
  white-space: nowrap;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
}
[data-theme="light"] .acfg-save-btn {
  background: rgba(241,245,249,0.7);
  border-color: rgba(99,102,241,0.12);
  color: rgba(100,116,139,0.55);
}
.acfg-save-btn--active {
  cursor: pointer;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border-color: transparent;
  color: #fff;
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.4),
    0 4px 18px rgba(99,102,241,0.35),
    inset 0 1px 0 rgba(255,255,255,0.15);
}
.acfg-save-btn--active:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.5),
    0 6px 24px rgba(99,102,241,0.45),
    inset 0 1px 0 rgba(255,255,255,0.18);
  transform: translateY(-1px);
}
.acfg-save-btn--active:active { transform: translateY(0); box-shadow: 0 2px 10px rgba(99,102,241,0.3); }
[data-theme="light"] .acfg-save-btn--active {
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  box-shadow: 0 4px 16px rgba(99,102,241,0.28), inset 0 1px 0 rgba(255,255,255,0.2);
}

.acfg-personal-banner {
  padding: 12px 16px; border-radius: 10px;
  font-size: 13px; font-weight: 400; line-height: 1.6; letter-spacing: 0.01em;
  background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.22);
  color: rgba(251,191,36,0.9); margin-bottom: 20px;
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
}
[data-theme="light"] .acfg-personal-banner { color: #b45309; background: rgba(245,158,11,0.07); }

.acfg-body { display: flex; gap: 28px; align-items: flex-start; }

.acfg-content { flex: 1; display: flex; flex-direction: column; gap: 20px; min-width: 0; }

/* ── Section card — matches dashboard aa-chart-card ── */
.acfg-section {
  position: relative;
  padding: 24px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  scroll-margin-top: 110px;
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.acfg-section::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
.acfg-section:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
[data-theme="light"] .acfg-section {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .acfg-section::before { opacity: 0.55; }
[data-theme="light"] .acfg-section:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 12px 32px rgba(99,102,241,0.06), 0 0 0 1px rgba(99,102,241,0.07);
}

.acfg-section-title {
  font-size: 14px; font-weight: 700; letter-spacing: 0.01em; line-height: 1.3;
  color: rgba(226,232,240,0.95);
  margin: 0 0 20px; display: flex; align-items: center; gap: 10px; position: relative;
}
[data-theme="light"] .acfg-section-title { color: #1e293b; }
.acfg-section-icon { width: 24px; height: 24px; object-fit: contain; filter: invert(1) brightness(2); }
[data-theme="light"] .acfg-section-icon { filter: none; }
.acfg-section-fields { display: flex; flex-direction: column; gap: 16px; position: relative; }

/* ── Divider ── */
.acfg-divider { padding: 12px 0 4px; border-top: 1px solid rgba(99,102,241,0.14); margin-top: 8px; }
[data-theme="light"] .acfg-divider { border-top-color: rgba(99,102,241,0.1); }
.acfg-divider span {
  font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(99,102,241,0.65);
}
[data-theme="light"] .acfg-divider span { color: rgba(99,102,241,0.55); }

/* ── Provider group — active gets a faint indigo left accent ── */
.acfg-provider-group { transition: opacity 0.22s ease; }
.acfg-provider-group--active {
  border-left: 2px solid rgba(99,102,241,0.3);
  padding-left: 12px;
  margin-left: -14px;
}
[data-theme="light"] .acfg-provider-group--active { border-left-color: rgba(99,102,241,0.22); }
.acfg-provider-group:not(.acfg-provider-group--active) { opacity: 0.38; }
.acfg-provider-group:not(.acfg-provider-group--active) .acfg-provider-group-fields { pointer-events: none; }
.acfg-provider-group-fields { display: flex; flex-direction: column; gap: 16px; margin-top: 12px; }

/* ── Fields ── */
.acfg-field { display: flex; flex-direction: column; gap: 6px; }
.acfg-field--disabled { opacity: 0.38; pointer-events: none; }
.acfg-label {
  font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
  color: rgba(148,163,184,0.65); line-height: 1;
}
[data-theme="light"] .acfg-label { color: rgba(71,85,105,0.75); }

/* ── Inputs & Selects (shared glass base) ── */
.acfg-input {
  padding: 9px 12px; border-radius: 8px;
  border: 1px solid rgba(99,102,241,0.2);
  background: rgba(6,9,22,0.6);
  color: rgba(226,232,240,0.92);
  font-size: 13px; font-weight: 400; line-height: 1.5; font-family: inherit;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
  width: 100%;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.3);
}
[data-theme="light"] .acfg-input {
  background: rgba(248,250,252,0.92);
  border-color: rgba(99,102,241,0.16);
  color: #1e293b;
  box-shadow: inset 0 1px 2px rgba(99,102,241,0.04);
}

/* placeholder */
.acfg-input::placeholder { color: rgba(100,116,139,0.6); }
[data-theme="light"] .acfg-input::placeholder { color: rgba(100,116,139,0.5); }

/* hover — lift border before focus */
.acfg-input:hover:not(:focus):not(:disabled) {
  border-color: rgba(99,102,241,0.32);
}
/* focus — purple glow ring */
.acfg-input:focus {
  border-color: rgba(99,102,241,0.6);
  background: rgba(8,12,30,0.72);
  box-shadow: 0 0 0 3px rgba(99,102,241,0.13), inset 0 1px 2px rgba(0,0,0,0.25);
}
[data-theme="light"] .acfg-input:focus {
  border-color: rgba(99,102,241,0.45);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
}

/* disabled */
.acfg-input:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: rgba(4,6,16,0.45);
}
[data-theme="light"] .acfg-input:disabled { background: rgba(241,245,249,0.7); }

/* select — custom chevron, no native arrow */
select.acfg-input {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 32px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(148,163,184,0.6)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 11px center;
  cursor: pointer;
}
[data-theme="light"] select.acfg-input {
  background-color: rgba(248,250,252,0.92);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(71,85,105,0.55)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 11px center;
}
select.acfg-input option {
  background: #0f1629;
  color: rgba(226,232,240,0.92);
}
[data-theme="light"] select.acfg-input option { background: #fff; color: #1e293b; }

/* number — hide spinner */
input[type="number"].acfg-input::-webkit-inner-spin-button,
input[type="number"].acfg-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
input[type="number"].acfg-input { -moz-appearance: textfield; }

.acfg-input--readonly {
  color: rgba(100,116,139,0.8); cursor: default;
  font-family: 'SF Mono', 'Fira Code', monospace; font-size: 12px;
  background: rgba(4,6,16,0.5);
}
[data-theme="light"] .acfg-input--readonly { background: rgba(241,245,249,0.8); color: #64748b; }

.acfg-input--error { border-color: rgba(239,68,68,0.7) !important; box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important; }
.acfg-input--prefixed { padding-left: 28px; }
.acfg-input-wrap { position: relative; }
.acfg-prefix { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 13px; color: rgba(100,116,139,0.7); font-weight: 600; }
.acfg-error { font-size: 11px; color: #f87171; font-weight: 500; letter-spacing: 0.01em; line-height: 1.4; }

/* ── Secret row ── */
.acfg-secret-wrap { display: flex; gap: 8px; align-items: center; }
.acfg-secret-wrap .acfg-input { flex: 1; }
.acfg-secret-toggle {
  padding: 8px 13px; border-radius: 7px;
  border: 1px solid rgba(99,102,241,0.22);
  background: rgba(6,9,22,0.55);
  color: rgba(148,163,184,0.8); font-size: 11px;
  font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  letter-spacing: 0.02em;
}
[data-theme="light"] .acfg-secret-toggle {
  background: rgba(241,245,249,0.92);
  border-color: rgba(99,102,241,0.16);
  color: #475569;
}
.acfg-secret-toggle:hover {
  border-color: rgba(99,102,241,0.5);
  background: rgba(99,102,241,0.12);
  color: #c7d2fe;
}
[data-theme="light"] .acfg-secret-toggle:hover {
  border-color: rgba(99,102,241,0.4);
  background: rgba(99,102,241,0.07);
  color: #4f46e5;
}

/* ── Toggle switch ── */
.acfg-toggle-wrap { display: flex; align-items: center; gap: 10px; }
.acfg-toggle {
  position: relative; width: 44px; height: 24px; border-radius: 12px;
  border: 1px solid rgba(99,102,241,0.28);
  background: rgba(6,9,22,0.65);
  cursor: pointer; transition: background 0.22s, border-color 0.22s, box-shadow 0.22s; padding: 0;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.4);
}
[data-theme="light"] .acfg-toggle {
  background: #e2e8f0; border-color: #cbd5e1;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.08);
}
.acfg-toggle--on {
  background: #16a34a; border-color: #15803d;
  box-shadow: 0 0 10px rgba(22,163,74,0.3), inset 0 1px 2px rgba(0,0,0,0.15);
}
[data-theme="light"] .acfg-toggle--on { background: #16a34a; border-color: #15803d; box-shadow: none; }
.acfg-toggle-knob {
  position: absolute; top: 2px; left: 2px; width: 18px; height: 18px;
  border-radius: 50%;
  background: rgba(148,163,184,0.85);
  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
  transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1), background 0.22s;
}
[data-theme="light"] .acfg-toggle-knob { background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
.acfg-toggle--on .acfg-toggle-knob { transform: translateX(20px); background: #fff; }
.acfg-toggle-label { font-size: 12px; font-weight: 500; letter-spacing: 0.01em; color: rgba(100,116,139,0.75); }
.acfg-toggle-label--on { color: #4ade80; font-weight: 600; }
[data-theme="light"] .acfg-toggle-label--on { color: #16a34a; }

/* ── Skeleton ── */
.acfg-skeleton-wrap { display: flex; flex-direction: column; gap: 20px; padding: 40px 0; }
.acfg-skeleton {
  height: 120px; border-radius: 16px;
  background: linear-gradient(135deg, rgba(18,24,48,0.65) 0%, rgba(12,16,36,0.6) 100%);
  border: 1px solid rgba(99,102,241,0.14);
  box-shadow: 0 0 28px rgba(99,102,241,0.06);
  animation: acfgPulse 1.4s ease-in-out infinite;
}
@keyframes acfgPulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.7; } }
`;
